import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Spinner } from 'react-bootstrap';
import { 
  FaUser, 
  FaEnvelope, 
  FaMapMarkerAlt, 
  FaCamera, 
  FaCog,
  FaCheckCircle,
  FaSave,
  FaKey
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import '../../../../assets/css/admin/dashboard/Settings.css';
import AdminLayout from '../../../../layouts/admin/AdminLayout';

const STG_BASE_URL = 'http://localhost:8000/api/admin';

const Settings = () => {
  // Logged in admin ki information (Login.js se)
  const adminData = JSON.parse(localStorage.getItem("adminData"));
  const STG_adminId = adminData?.id;

  // Admin Profile State
  const [adminProfile, setAdminProfile] = useState({
    name: '',
    email: '',
    address: '',
    profilePicture: null,
  });

  // Selected file (upload ke liye) preview se separate
  const [STG_selectedImage, setSTG_selectedImage] = useState(null);

  const [STG_loading, setSTG_loading] = useState(true);
  const [STG_saving, setSTG_saving] = useState(false);
  const [STG_changingPassword, setSTG_changingPassword] = useState(false);

  // ===================== VIEW PROFILE (READ) =====================
  const STG_fetchProfile = () => {
    setSTG_loading(true);

    const formData = new FormData();
    formData.append('id', STG_adminId);

    fetch(`${STG_BASE_URL}/view-profile`, {
      method: 'POST',
      body: formData
    })
      .then((res) => res.json())
      .then((response) => {
        if (response.success) {
          const admin = response.admin;
          setAdminProfile({
            name: admin.name || '',
            email: admin.email || '',
            address: admin.address || '',
            profilePicture: admin.image ? `http://localhost:8000/storage/${admin.image}` : null
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error!',
            text: response.message || 'Unable to load profile',
            confirmButtonColor: '#9f7aea'
          });
        }
      })
      .catch((error) => {
        console.error(error);
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'Something went wrong while loading your profile',
          confirmButtonColor: '#9f7aea'
        });
      })
      .finally(() => {
        setSTG_loading(false);
      });
  };

  useEffect(() => {
    if (!STG_adminId) {
      Swal.fire({
        icon: "error",
        title: "Session Expired",
        text: "Please login again."
      });
      return;
    }

    STG_fetchProfile();
  }, [STG_adminId]);

  // Handle Profile Image Upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5000000) {
        Swal.fire({
          icon: 'error',
          title: 'File Too Large!',
          text: 'Please upload an image smaller than 5MB',
          confirmButtonColor: '#9f7aea'
        });
        return;
      }

      setSTG_selectedImage(file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setAdminProfile({ ...adminProfile, profilePicture: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  // ===================== UPDATE PROFILE =====================
  const handleUpdateProfile = () => {
    if (!adminProfile.name || !adminProfile.email) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error!',
        text: 'Please fill all required fields',
        confirmButtonColor: '#9f7aea'
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(adminProfile.email)) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Email!',
        text: 'Please enter a valid email address',
        confirmButtonColor: '#9f7aea'
      });
      return;
    }

    setSTG_saving(true);

    const formData = new FormData();
    formData.append('id', STG_adminId);
    formData.append('name', adminProfile.name);
    formData.append('email', adminProfile.email);
    formData.append('address', adminProfile.address);
    if (STG_selectedImage) {
      formData.append('file', STG_selectedImage);
    }

    fetch(`${STG_BASE_URL}/update-profile`, {
      method: 'POST',
      body: formData
    })
      .then((res) => res.json())
      .then((response) => {
        if (response.success) {
          Swal.fire({
            icon: 'success',
            title: 'Profile Updated!',
            text: 'Your profile has been updated successfully',
            confirmButtonColor: '#9f7aea',
            timer: 2000,
            showConfirmButton: false
          });
          STG_fetchProfile();
          setSTG_selectedImage(null);
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error!',
            text: response.message || 'Unable to update profile',
            confirmButtonColor: '#9f7aea'
          });
        }
      })
      .catch((error) => {
        console.error(error);
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'Something went wrong while updating your profile',
          confirmButtonColor: '#9f7aea'
        });
      })
      .finally(() => {
        setSTG_saving(false);
      });
  };

  // ===================== CHANGE PASSWORD =====================
  const handleChangePassword = () => {
    Swal.fire({
      title: 'Change Password',
      html: `
        <input type="password" id="current-password" class="swal2-input" placeholder="Current Password">
        <input type="password" id="new-password" class="swal2-input" placeholder="New Password">
        <input type="password" id="confirm-password" class="swal2-input" placeholder="Confirm Password">
      `,
      confirmButtonText: 'Update Password',
      confirmButtonColor: '#9f7aea',
      showCancelButton: true,
      preConfirm: () => {
        const current = document.getElementById('current-password').value;
        const newPass = document.getElementById('new-password').value;
        const confirm = document.getElementById('confirm-password').value;

        if (!current || !newPass || !confirm) {
          Swal.showValidationMessage('Please fill all fields');
          return false;
        }
        if (newPass.length < 8) {
          Swal.showValidationMessage('Password must be at least 8 characters long');
          return false;
        }
        if (newPass !== confirm) {
          Swal.showValidationMessage('Passwords do not match');
          return false;
        }
        return { current, newPass };
      }
    }).then((result) => {
      if (result.isConfirmed) {
        setSTG_changingPassword(true);

        const formData = new FormData();
        formData.append('id', STG_adminId);
        formData.append('currentPassword', result.value.current);
        formData.append('newPassword', result.value.newPass);

        fetch(`${STG_BASE_URL}/change-password`, {
          method: 'POST',
          body: formData
        })
          .then((res) => res.json())
          .then((response) => {
            if (response.success) {
              Swal.fire({
                icon: 'success',
                title: 'Password Changed!',
                text: 'Your password has been updated successfully',
                confirmButtonColor: '#9f7aea',
                timer: 2000,
                showConfirmButton: false
              });
            } else {
              Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: response.message || 'Current password is incorrect',
                confirmButtonColor: '#9f7aea'
              });
            }
          })
          .catch((error) => {
            console.error(error);
            Swal.fire({
              icon: 'error',
              title: 'Error!',
              text: 'Something went wrong while updating your password',
              confirmButtonColor: '#9f7aea'
            });
          })
          .finally(() => {
            setSTG_changingPassword(false);
          });
      }
    });
  };

  if (STG_loading) {
    return (
      <AdminLayout>
        <div className="stg-settings-page d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <Spinner animation="border" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
    <div className="stg-settings-page">
      <Container fluid className="stg-container">
        {/* Page Header */}
        <div className="stg-page-header">
          <div className="stg-header-content">
            <h1 className="stg-page-title">
              <FaCog className="stg-title-icon" />
              Settings
            </h1>
            <p className="stg-page-subtitle">Manage system & admin preferences</p>
          </div>
        </div>

        {/* Settings Sections */}
        <Row className="stg-sections-row">
          <Col lg={12}>
            {/* Admin Profile Section */}
            <Card className="stg-section-card">
              <Card.Header className="stg-card-header">
                <h3 className="stg-section-title">
                  <FaUser className="stg-section-icon" />
                  Admin Profile
                </h3>
              </Card.Header>
              <Card.Body className="stg-card-body">
                <Row>
                  <Col md={6}>
                    <Form.Group className="stg-form-group">
                      <Form.Label className="stg-form-label">
                        <FaUser className="stg-label-icon" />
                        Name
                      </Form.Label>
                      <Form.Control
                        type="text"
                        className="stg-form-input"
                        value={adminProfile.name}
                        onChange={(e) => setAdminProfile({...adminProfile, name: e.target.value})}
                        placeholder="Enter admin name"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="stg-form-group">
                      <Form.Label className="stg-form-label">
                        <FaEnvelope className="stg-label-icon" />
                        Email
                      </Form.Label>
                      <Form.Control
                        type="email"
                        className="stg-form-input"
                        value={adminProfile.email}
                        onChange={(e) => setAdminProfile({...adminProfile, email: e.target.value})}
                        placeholder="Enter email address"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="stg-form-group">
                      <Form.Label className="stg-form-label">
                        <FaMapMarkerAlt className="stg-label-icon" />
                        Address
                      </Form.Label>
                      <Form.Control
                        type="text"
                        className="stg-form-input"
                        value={adminProfile.address}
                        onChange={(e) => setAdminProfile({...adminProfile, address: e.target.value})}
                        placeholder="Enter address"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="stg-form-group">
                      <Form.Label className="stg-form-label">
                        <FaCamera className="stg-label-icon" />
                        Profile Picture
                      </Form.Label>
                      <div className="stg-upload-wrapper">
                        <input
                          type="file"
                          id="profile-upload"
                          className="stg-file-input"
                          accept="image/*"
                          onChange={handleImageUpload}
                        />
                        <label htmlFor="profile-upload" className="stg-upload-btn">
                          <FaCamera className="stg-upload-icon" />
                          Upload Image
                        </label>
                        {adminProfile.profilePicture && (
                          <div className="stg-preview-wrapper">
                            <img 
                              src={adminProfile.profilePicture} 
                              alt="Preview" 
                              className="stg-preview-image"
                            />
                          </div>
                        )}
                      </div>
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <div className="stg-action-buttons">
                      <Button className="stg-btn stg-btn-primary" onClick={handleUpdateProfile} disabled={STG_saving}>
                        <FaCheckCircle className="stg-btn-icon" />
                        {STG_saving ? 'Updating...' : 'Update Profile'}
                      </Button>
                      <Button className="stg-btn stg-btn-secondary" onClick={handleChangePassword} disabled={STG_changingPassword}>
                        <FaKey className="stg-btn-icon" />
                        {STG_changingPassword ? 'Updating...' : 'Change Password'}
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
    </AdminLayout>
  );
};

export default Settings;